import { Stack, Typography } from "@mui/material";
import { ReuseInput, ReuseModal } from "../../../../ui";
import {
  Integration,
  IntegrationDisplayName,
  ModalSize,
} from "../../../../../types/enums";
import { EMAIL_REGEX } from "../../../../../utils/regex";
import { useState } from "react";

const getSendText = (crmType: string) =>
  `Enter the email address of the person responsible for the ${crmType} connection. They'll receive a` +
  ` link to securely submit their credentials to complete the data sync setup.`;

const getTextMapper = (crmType: string) => ({
  add: {
    title: "Credentials Request",
    text:
      `Enter your client's email address. After the campaign is set up, they'll get a link to submit their` +
      ` ${crmType} credentials and configure their data sync.`,
    color: "#1C3C6C",
    btnText: "Add Email",
    isInputDisabled: false,
  },
  send: {
    title: "Credentials Request",
    text: getSendText(crmType),
    color: "#1C3C6C",
    btnText: "Add Email",
    isInputDisabled: false,
  },
  resend: {
    title: "Credentials Request",
    text: getSendText(crmType),
    color: "#1C3C6C",
    btnText: "Send Request",
    isInputDisabled: false,
  },
  remove: {
    title: "Delete Email for Credentials Request",
    text: "Are you sure you want to delete email? No credentials will be shared.",
    color: "#C0392B",
    btnText: "Delete Email",
    isInputDisabled: true,
  },
  cancel: {
    title: "Cancel Request",
    text: "Are you sure you want to сancel request? No credentials will be shared.",
    color: "#C0392B",
    btnText: "Cancel Request",
    isInputDisabled: true,
  },
});

type Props = {
  currentEmail: string;
  setCurrentEmail: (email: string) => void;
  onClose: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  modalType: keyof ReturnType<typeof getTextMapper>;
  crmType: string;
};

export const CRMRequestModal = ({
  currentEmail,
  setCurrentEmail,
  onClose,
  isLoading,
  isDisabled,
  modalType,
  crmType,
}: Props) => {
  const [email, setEmail] = useState<string>(currentEmail);
  const [isError, setIsError] = useState<boolean>(false);

  const displayName = IntegrationDisplayName[crmType as Integration] ?? crmType;

  const inputChangeHandler = (field: string, value: string) => {
    setIsError(!value.trim() || !EMAIL_REGEX.test(value));
    setEmail(value);
  };

  const submitHandler = () => {
    setCurrentEmail(email);
  };

  const { title, text, btnText, color, isInputDisabled } =
    getTextMapper(displayName)[modalType];

  return (
    <ReuseModal
      isOpen={true}
      title={title}
      onClose={onClose}
      onSubmit={submitHandler}
      submitBtnText={btnText}
      submitBtnColor={color}
      maxWidth={ModalSize.small}
      isDisabledCancelBtn={isDisabled || isLoading}
      isDisabled={!email || isError || isLoading}
      isLoading={isLoading}
    >
      <Stack gap={"32px"} paddingBottom={"24px"}>
        <Typography variant="subtitle1" sx={{ color: "#464646" }}>
          {text}
        </Typography>
        <ReuseInput
          name="email"
          handleInputChange={inputChangeHandler}
          errorMessage={isError ? "Valid email is required" : ""}
          value={email}
          label="Email"
          placeholder="example@email.com"
          isError={isError}
          isDisabled={isInputDisabled || isLoading}
        />
      </Stack>
    </ReuseModal>
  );
};
